import { useState, useRef, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Lock,
  Sparkles,
  Video,
  FileText,
  Headphones,
  Image as ImageIcon,
  MousePointerClick,
  Download,
  PlayCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
} from 'lucide-react';
import { SEO } from '@/components/SEO';
import { useAllContentByCategory, type ContentItem } from '@/hooks/useContent';
import { RegisterToViewModal } from '@/components/public/RegisterToViewModal';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const typeIcon = (t: string | null) => {
  switch (t) {
    case 'video': return Video;
    case 'reading': return FileText;
    case 'infographic': return ImageIcon;
    case 'lia-class': return Sparkles;
    case 'interactive': return MousePointerClick;
    case 'resource': return Download;
    case 'audio': return Headphones;
    default: return PlayCircle;
  }
};

const typeLabel = (t: string | null) => {
  switch (t) {
    case 'video': return 'Video';
    case 'reading': return 'Lectura';
    case 'infographic': return 'Infografía';
    case 'lia-class': return 'Clase con LIA';
    case 'interactive': return 'Interactivo';
    case 'resource': return 'Recurso';
    case 'audio': return 'Audio';
    default: return 'Contenido';
  }
};

function ContentCard({ item, onClick }: { item: ContentItem; onClick: () => void }) {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = typeIcon(item.content_type);

  return (
    <div
      className="group relative flex-shrink-0 w-[280px] cursor-pointer snap-start"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <div
        className={cn(
          'relative rounded-xl overflow-hidden transition-all duration-500 bg-card border border-border',
          isHovered && 'scale-105 z-20 shadow-2xl shadow-black/50 border-primary/50'
        )}
      >
        <div className="relative aspect-video overflow-hidden bg-muted">
          {item.thumbnail_url ? (
            <img
              src={item.thumbnail_url}
              alt={item.name}
              loading="lazy"
              className={cn(
                'absolute inset-0 w-full h-full object-cover transition-all duration-700',
                isHovered && 'scale-110'
              )}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
              <Icon className="w-12 h-12 text-primary/60" />
            </div>
          )}

          <div
            className={cn(
              'absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent transition-opacity duration-300',
              isHovered ? 'opacity-100' : 'opacity-60'
            )}
          />

          {/* Type badge */}
          <div className="absolute top-3 left-3 flex items-center gap-1.5">
            <div className="px-2 py-1 rounded bg-black/70 backdrop-blur-sm text-white text-xs font-medium flex items-center gap-1">
              <Icon className="w-3 h-3" />
              {typeLabel(item.content_type)}
            </div>
            {item.access_level === 'subscription' && (
              <div className="px-2 py-1 rounded bg-gradient-to-r from-primary to-secondary text-white text-xs font-semibold flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Pro
              </div>
            )}
          </div>

          {item.duration && (
            <div className="absolute top-3 right-3 px-2 py-1 rounded bg-black/70 backdrop-blur-sm text-white text-xs font-medium flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {item.duration}
            </div>
          )}

          {/* Lock overlay on hover */}
          <div
            className={cn(
              'absolute inset-0 flex flex-col items-center justify-center gap-2 transition-opacity duration-300',
              isHovered ? 'opacity-100' : 'opacity-0'
            )}
          >
            <div className="w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <span className="text-white text-sm font-medium">
              {item.access_level === 'subscription' ? 'Suscríbete para ver' : 'Regístrate para ver'}
            </span>

          </div>
        </div>

        <div className="p-4">
          <h4 className="font-semibold text-foreground text-sm line-clamp-2 group-hover:text-primary transition-colors">
            {item.name}
          </h4>
          {item.description && (
            <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">
              {item.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryRow({
  category,
  items,
  onSelect,
}: {
  category: string;
  items: ContentItem[];
  onSelect: (item: ContentItem) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -600 : 600,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      return () => ref.removeEventListener('scroll', checkScroll);
    }
  }, [items.length]);

  return (
    <div className="relative group/row mb-8">
      <div className="flex items-end justify-between mb-4 px-2">
        <h2 className="text-xl md:text-2xl font-bold text-foreground">{category}</h2>
        <span className="text-sm text-muted-foreground">
          {items.length} {items.length === 1 ? 'recurso' : 'recursos'}
        </span>
      </div>

      <div className="relative">
        {canScrollLeft && (
          <Button
            variant="secondary"
            size="icon"
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-30 h-12 w-12 rounded-full opacity-0 group-hover/row:opacity-100 transition-opacity shadow-lg"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
        )}
        {canScrollRight && (
          <Button
            variant="secondary"
            size="icon"
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-30 h-12 w-12 rounded-full opacity-0 group-hover/row:opacity-100 transition-opacity shadow-lg"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>
        )}

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scroll-smooth snap-x scrollbar-hide px-2 py-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {items.map((item) => (
            <ContentCard key={item.id} item={item} onClick={() => onSelect(item)} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PublicContentPage() {
  const { data, isLoading } = useAllContentByCategory();
  const [selected, setSelected] = useState<ContentItem | null>(null);

  const categories = data ? Object.entries(data) : [];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Biblioteca de contenido IA — LÍDER IA"
        description="Explora videos, lecturas, infografías y clases interactivas sobre IA y liderazgo. Crea tu cuenta gratis para acceder."
        path="/content"
      />

      <div className="px-2 md:px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="sr-only">Biblioteca de contenido de IA</h1>
          {isLoading && (
            <div className="space-y-8">
              {[1, 2].map((s) => (
                <div key={s} className="space-y-4">
                  <Skeleton className="h-7 w-48" />
                  <div className="flex gap-4 overflow-hidden">
                    {[1, 2, 3, 4].map((c) => (
                      <Skeleton key={c} className="aspect-video w-[280px] flex-shrink-0" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && categories.length === 0 && (
            <div className="text-center text-muted-foreground py-12">
              No hay contenido disponible por el momento.
            </div>
          )}

          {!isLoading && categories.map(([category, items]) => (
            <CategoryRow
              key={category}
              category={category}
              items={items}
              onSelect={setSelected}
            />
          ))}
        </div>
      </div>

      <RegisterToViewModal
        open={!!selected}
        onOpenChange={(open) => !open && setSelected(null)}
        contentName={selected?.name}
        accessLevel={selected?.access_level}
      />

    </div>
  );
}
